import React, {useEffect, useState} from 'react';
import {ConfigProvider, Spin} from "antd";
import {usePostApiAuthLoginMutation} from "../api/services/auth";
import {useLazyGetApiTenantGetTenantInfoQuery} from "../api/services/Tenant";
import {setError, setLoading, setTheme, setThemeColors} from "../features/app/appSlice";
import {t} from "i18next";
import {Redirect, useLocation} from "react-router-dom";
import {setTenantInfo, setTenantLoadingState} from "../app/persistedSlice";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../app/store";
import {useDocumentTitle} from "usehooks-ts";
import {useFavicon} from "../hooks/useFavicon";

export interface ITenantManager {
    children: React.ReactNode;
}

const TenantManager: React.FC<ITenantManager> = ({children}) => {
    // Hangi tenant kimliği için sorgu tamamlandı (başarılı ya da başarısız).
    // null: henüz denenmedi.
    const [attemptedTenantId, setAttemptedTenantId] = useState<string | null>(null);
    const [fetchTenantInfo] = useLazyGetApiTenantGetTenantInfoQuery()
    // useHistory().location konum değişikliğine abone olmaz; istemci tarafı
    // gezinmede bileşen yeniden render olmadığı için tenant bilgisi hiç
    // yenilenmiyordu. useLocation abone olur.
    const location = useLocation()
    const dispatch = useDispatch();
    const theme = useSelector((state: RootState) => state.app?.themeConfig);
    const tenantInfo = useSelector((state: RootState) => state.persisted?.tenantInfo);

    useDocumentTitle(tenantInfo?.name ?? "", {preserveTitleOnUnmount: true});
    // Favicon tanımlı değilse istek atma; aksi halde ".../companies/undefined"
    // adresine başarısız bir istek gidiyor (yeni kurulumlarda logo yüklü olmaz).
    const faviconName = (tenantInfo as any)?.faviconName
    useFavicon(faviconName
        ? `${process.env.REACT_APP_API_IMAGE_URL}/api/image/images/companies/${faviconName}`
        : "")

    const getTenantInfo = async (id: string) => {
        if(!id || id === "404") return;
        
        dispatch(setTenantLoadingState(false))
        try {
            var tentantData = await fetchTenantInfo({
                companyId: id
            }).unwrap()

            setTenantColors((tentantData as any)?.settings ?? [])

            dispatch(setTenantInfo(tentantData));
            dispatch(setTenantLoadingState(true))
            return true
        } catch (err: any) {
            return null
        }
    }

    const setTenantColors = (settings: any[]) => {
        let primaryColor = settings.find(k => k.key === "primaryColor")?.value ?? "#89A8B2"
        let secondaryColor = settings.find(k => k.key === "secondaryColor")?.value ?? "#B3C8CF"
        let ternaryColor = settings.find(k => k.key === "ternaryColor")?.value ?? "#F1F0E8"
        let textColor = settings.find(k => k.key === "textColor")?.value ?? "#000000"

        dispatch(setThemeColors({
            primaryColor: primaryColor,
            secondaryColor: secondaryColor,
            ternaryColor: ternaryColor,
            textColor: textColor
        }))

        dispatch(setTheme({
            token: {
                "colorBgElevated": ternaryColor,
                "colorPrimary": primaryColor,
                "colorPrimaryBg": primaryColor,
                "colorPrimaryBgHover": primaryColor,
                "colorPrimaryHover": primaryColor,
                "colorText": textColor,
                "colorBorder": secondaryColor,
                "colorPrimaryBorder": secondaryColor,
                "fontFamily": "Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'"
            },
            components: {
                Layout: {
                    "siderBg": primaryColor,
                    "triggerColor": "#000000",
                    "bodyBg": ternaryColor,
                    "headerBg": primaryColor,
                    "headerColor": secondaryColor,
                },
                Menu: {
                    "darkItemBg": primaryColor,
                    "darkItemColor": textColor,
                    "darkItemHoverColor": textColor,
                    "darkItemSelectedColor": textColor,
                    "darkPopupBg": primaryColor,

                },
                Button: {
                    "defaultColor": textColor,
                    "defaultActiveColor": textColor,
                    "primaryColor": textColor,
                },
                Progress: {
                    "circleTextFontSize": "10px"
                },
                Slider: {
                    "handleColor": "green",
                },
                Tabs: {
                    "itemActiveColor": textColor,
                    "itemSelectedColor": textColor,
                    "itemHoverColor": textColor,
                    "itemColor": textColor,
                },
                DatePicker: {
                    "cellHoverBg": secondaryColor,
                    "cellActiveWithRangeBg": secondaryColor,
                    "hoverBg": secondaryColor,
                },
            },
        }))

    }

    const pathTenantId: string = location.pathname.split("/")?.[1]
    const needsTenant = Boolean(pathTenantId) && pathTenantId !== "404"

    useEffect(() => {
        getTenantInfo(pathTenantId).finally(() => setAttemptedTenantId(pathTenantId))
        // tenantInfo bilerek bağımlılık listesinde değil: her yüklemede yeni
        // nesne döndüğü için sonsuz döngüye girerdi.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    // Bu karar bilerek render sırasında hesaplanıyor, state ile değil.
    // Adres değiştiğinde alt bileşenler o render turunda çalışır; state ile
    // kapatmak bir adım geç kalır ve /:id/login rotası henüz yüklenmemiş
    // tenantInfo ile doğrulama yapıp 404'e düşer.
    const tenantMatchesPath = (tenantInfo as any)?.id === pathTenantId
    const tenantAttempted = attemptedTenantId === pathTenantId

    if (needsTenant && !tenantMatchesPath && !tenantAttempted) {
        return <Spin percent={"auto"} size="large" fullscreen={true}/>
    }

    return <>{children}</>;
};

export default TenantManager;